/**
 * A porta entre a renovação e estratégia da frota (módulo 20; 0107) e o
 * servidor: a lista da frota, a economia de uma máquina, os estudos com as
 * premissas e as alternativas, os critérios, as propostas e o plano. A mesa e
 * o celular usam a mesma.
 */
import { comCorpo, pedir } from '@/lib/pedido';

export type Alternativa = 'manter' | 'reformar' | 'substituir_comprando' | 'substituir_alugando' | 'vender_sem_repor' | 'adicional_do_tipo';
export type SituacaoDoEstudo = 'rascunho' | 'concluido' | 'arquivado';
export type SituacaoDaProposta = 'proposta' | 'priorizada' | 'aprovada' | 'recusada' | 'adiada';
export type TipoDeProposta = 'substituir_comprando' | 'substituir_alugando' | 'reformar' | 'comprar_adicional' | 'alugar_adicional' | 'vender_sem_repor';
export type Risco = 'baixo' | 'medio' | 'alto';
export type GrupoDaPremissa = 'demanda' | 'maquina_atual' | 'reforma' | 'compra' | 'locacao';
export type OrigemDaPremissa = 'sistema' | 'informada' | 'orcamento';
export type OrdemDaFrota = 'custo' | 'tendencia' | 'margem' | 'disponibilidade' | 'idade' | 'vida' | 'codigo';
export type FiltroDaFrota = 'candidatas' | 'tendencia_alta' | 'horas_insuficientes' | 'em_estudo' | 'sem_estudo';

export interface Tendencia {
  readonly valor: number | null;
  readonly sentido: 'sobe' | 'desce' | 'estavel' | null;
  readonly meses: number;
  readonly abstencao: string | null;
}

export interface Marca {
  readonly marcada: boolean;
  readonly motivo: string;
}

export interface Premissas {
  readonly inclui: readonly string[];
  readonly naoInclui: readonly string[];
  readonly completo: boolean;
}

export interface LinhaDaFrota {
  readonly equipamentoId: string;
  readonly codigo: string;
  readonly descricao: string;
  readonly tipo: string | null;
  readonly modelo: string | null;
  readonly idade: { readonly anos: number | null; readonly pelo: 'fabricacao' | 'aquisicao' | null };
  readonly horimetro: number | null;
  readonly vidaConsumida: number | null;
  readonly horasProdutivas: number;
  readonly horasInsuficientes: boolean;
  readonly custoTotalPorHora: number | null;
  readonly tendencia: Tendencia;
  readonly margemPorHora: number | null;
  readonly disponibilidade: number | null;
  readonly completo: boolean;
  readonly estudo: { readonly id: string; readonly numero: number; readonly situacao: SituacaoDoEstudo; readonly alternativaEscolhida: Alternativa | null } | null;
  readonly candidata: Marca | null;
}

export interface Frota {
  readonly dataBase: string;
  readonly janela: { readonly de: string; readonly ate: string; readonly meses: number };
  readonly parametros: { readonly mesesMinimosTendencia: number; readonly limiarCandidataPct: number; readonly horasMinimasJanela: number; readonly definidosPelaEmpresa: boolean };
  readonly indicadores: {
    readonly maquinas: number;
    readonly custoTotalPorHoraDaFrota: number | null;
    readonly comHorasSuficientes: number;
    readonly tendenciaDeAlta: number;
    readonly semTendencia: number;
    readonly emEstudo: number;
    readonly candidatas: number;
    readonly horasInsuficientes: number;
  };
  readonly tipos: readonly string[];
  readonly total: number;
  readonly pagina: number;
  readonly porPagina: number;
  readonly linhas: readonly LinhaDaFrota[];
  readonly comReceita: boolean;
  readonly frases: { readonly candidata: string; readonly valor: string; readonly mesAberto: string };
}

export interface Economia {
  readonly meses: number;
  readonly de: string | null;
  readonly ate: string | null;
  readonly horasProdutivas: number;
  readonly horasMotor: number;
  readonly custoDireto: number;
  readonly custoDeManutencao: number;
  readonly combustivel: number;
  readonly maoDeObraOperacao: number;
  readonly depreciacao: number;
  readonly indireto: number;
  readonly custoTotal: number;
  readonly custoTotalPorHora: number | null;
  readonly horasInsuficientes: boolean;
  readonly omPorHora: number | null;
  readonly combustivelPorHora: number | null;
  readonly operadorPorHora: number | null;
  readonly manutencaoPorHora: number | null;
  readonly receita: number | null;
  readonly margem: number | null;
  readonly margemPorHora: number | null;
  readonly disponibilidade: number | null;
  readonly horasProgramadas: number | null;
  readonly premissas: Premissas;
}

export interface MesDaSerie {
  readonly competencia: string;
  readonly emAndamento: boolean;
  readonly horasProdutivas: number;
  readonly custoTotal: number;
  readonly custoTotalPorHora: number | null;
  readonly manutencaoPorHora: number | null;
  readonly receita: number | null;
  readonly margemPorHora: number | null;
  readonly disponibilidade: number | null;
}

export interface ValorContabil {
  readonly valor: number | null;
  readonly aquisicao: number | null;
  readonly residual: number | null;
  readonly inicio: string | null;
  readonly vidaUtilMeses: number | null;
  readonly mesesDecorridos: number | null;
  readonly vidaConsumida: number | null;
}

export interface Indicador {
  readonly valor: number | null;
  readonly base: number;
  readonly premissas: Premissas;
}

export interface Confiabilidade {
  readonly mtbf: Indicador;
  readonly mttr: Indicador;
  readonly falhasNaJanela: number;
  readonly falhasClassificadas: number;
}

export interface Faixas {
  readonly faixas: readonly { readonly de: number; readonly ate: number; readonly horasProdutivas: number; readonly custoDeManutencao: number; readonly manutencaoPorHora: number | null; readonly meses: number }[];
  readonly semLeitura: number;
}

export interface EstudoDaMaquina {
  readonly id: string;
  readonly numero: number;
  readonly situacao: SituacaoDoEstudo;
  readonly alternativaEscolhida: Alternativa | null;
  readonly nomeDaAlternativa: string | null;
  readonly criadoEm: string;
  readonly concluidoEm: string | null;
}

export interface HistoricoDaMaquina {
  readonly maquina: { readonly id: string; readonly codigo: string; readonly descricao: string; readonly tipo: string | null; readonly modelo: string | null };
  readonly dataBase: string;
  readonly economia: Economia;
  readonly serie: readonly MesDaSerie[];
  readonly tendencia: Tendencia & { readonly minimo: number };
  readonly faixas: Faixas;
  readonly valorContabil: ValorContabil;
  readonly idade: LinhaDaFrota['idade'];
  readonly horimetro: number | null;
  readonly confiabilidade: Confiabilidade;
  readonly comReceita: boolean;
  readonly estudos: readonly EstudoDaMaquina[];
  readonly frases: { readonly valor: string; readonly mesAberto: string };
}

export interface ResultadoDaAlternativa {
  readonly codigo: Alternativa;
  readonly nome: string;
  readonly calculada: boolean;
  readonly falta: readonly string[];
  readonly naoSeAplica: string | null;
  readonly total: number | null;
  readonly custoPorHora: number | null;
  readonly valorPresente: number | null;
  readonly caixa: readonly number[];
  readonly indisponibilidadeHoras: number | null;
  readonly indisponibilidadeValor: number | null;
  readonly partes: readonly { readonly rotulo: string; readonly valor: number }[];
  readonly notas: readonly string[];
}

export interface PremissaDoEstudo {
  readonly codigo: string;
  readonly rotulo: string;
  readonly unidade: string;
  readonly ondeEntra: string;
  readonly grupo: GrupoDaPremissa;
  readonly soInformada: boolean;
  readonly simOuNao: boolean;
  readonly valor: number | null;
  readonly fonte: string | null;
  readonly origem: OrigemDaPremissa | null;
  readonly demonstracao: boolean;
  readonly gravadoPor: string | null;
  readonly atualizadoEm: string | null;
}

export interface NumerosDoEstudo {
  readonly versaoDaConta: string;
  readonly calculadoEm: string;
  readonly dataBase: string;
  readonly horizonteMeses: number;
  readonly economia: Economia;
  readonly tendencia: Tendencia;
  readonly valorContabil: ValorContabil;
  readonly capacidade: { readonly outrasDoTipo: number | null; readonly destaMaquina: number | null; readonly maquinasDoTipo: number };
  readonly alternativas: readonly ResultadoDaAlternativa[];
  readonly melhor: Alternativa | null;
  readonly candidata: Marca;
  readonly naoInclui: readonly string[];
  readonly frases: { readonly valor: string; readonly candidata: string };
}

export interface Estudo {
  readonly estudo: {
    readonly id: string;
    readonly numero: number;
    readonly equipamentoId: string;
    readonly codigo: string;
    readonly descricao: string;
    readonly tipo: string | null;
    readonly dataBase: string;
    readonly horizonteMeses: number;
    readonly situacao: SituacaoDoEstudo;
    readonly orcamentoId: string | null;
    readonly cenarioId: string | null;
    readonly alternativaEscolhida: Alternativa | null;
    readonly nomeDaAlternativa: string | null;
    readonly conclusao: string | null;
    readonly criadoPor: string | null;
    readonly concluidoPor: string | null;
    readonly concluidoEm: string | null;
    readonly arquivadoEm: string | null;
    readonly motivoDoArquivo: string | null;
    readonly criadoEm: string;
  };
  readonly numeros: NumerosDoEstudo;
  readonly congelado: boolean;
  readonly premissas: readonly PremissaDoEstudo[];
  readonly serie: readonly MesDaSerie[] | null;
  readonly faixas: Faixas | null;
  readonly confiabilidade: Confiabilidade | null;
  readonly idade: LinhaDaFrota['idade'] | null;
  readonly horimetro: number | null;
  readonly orcamento: { readonly permitido: boolean; readonly versao: { readonly id: string; readonly versao: number; readonly nome: string } | null; readonly cenarios: readonly { readonly id: string; readonly nome: string }[] };
  readonly propostas: readonly { readonly id: string; readonly numero: number; readonly situacao: SituacaoDaProposta }[];
  readonly podeGravar: boolean;
  readonly podePropor: boolean;
  readonly frases: { readonly valor: string; readonly candidata: string; readonly locacao: string; readonly concluido: string; readonly mesAberto: string };
}

export interface EstudoNaLista {
  readonly id: string;
  readonly numero: number;
  readonly equipamentoId: string;
  readonly codigo: string;
  readonly descricao: string;
  readonly situacao: SituacaoDoEstudo;
  readonly horizonteMeses: number;
  readonly dataBase: string;
  readonly nomeDaAlternativa: string | null;
  readonly criadoPor: string | null;
  readonly criadoEm: string;
  readonly concluidoEm: string | null;
  readonly candidata: Marca | null;
}

export interface Criterio {
  readonly id: string;
  readonly nome: string;
  readonly descricao: string;
  readonly peso: number;
  readonly desde: string;
  readonly ate: string | null;
  readonly situacao: 'proposto' | 'aprovado' | 'retirado';
  readonly vigente: boolean;
  readonly propostoPor: string | null;
  readonly aprovadoPor: string | null;
  readonly aprovadoEm: string | null;
  readonly nota: string | null;
  readonly retiradoEm: string | null;
  readonly motivoDaRetirada: string | null;
  readonly podeAprovar: boolean;
  readonly impedimento: string | null;
}

export interface Proposta {
  readonly id: string;
  readonly numero: number;
  readonly tipo: TipoDeProposta;
  readonly nomeDoTipo: string;
  readonly equipamentoId: string | null;
  readonly codigo: string | null;
  readonly tipoDeMaquina: string | null;
  readonly estudoId: string | null;
  readonly estudoNumero: number | null;
  readonly descricao: string;
  readonly investimento: number | null;
  readonly compromissoMensal: number | null;
  readonly mesesDeCompromisso: number | null;
  readonly dataPretendida: string;
  readonly risco: Risco;
  readonly justificativaDoRisco: string;
  readonly situacao: SituacaoDaProposta;
  readonly propostaPor: string | null;
  readonly decididaPor: string | null;
  readonly decididaEm: string | null;
  readonly nota: string | null;
  readonly criadoEm: string;
  readonly notas: readonly { readonly criterioId: string; readonly nota: number; readonly justificativa: string; readonly dadaPor: string | null }[];
  readonly pontuacao: number | null;
  readonly semNota: readonly string[];
  readonly noOrcamento: readonly { readonly exercicio: number; readonly versao: number; readonly situacao: string }[];
  readonly podeDecidir: boolean;
  readonly impedimento: string | null;
}

export interface Propostas {
  readonly criterios: readonly { readonly id: string; readonly nome: string; readonly peso: number; readonly vigente: boolean }[];
  readonly temLicencaDeAprovar: boolean;
  readonly propostas: readonly Proposta[];
}

export interface LinhaDoPlano {
  readonly id: string;
  readonly posicao: number;
  readonly pontuacao: number | null;
  readonly semNota: readonly string[];
  readonly consumo: number;
  readonly acumulado: number | null;
  readonly cabe: boolean | null;
  readonly motivo: string;
  readonly proposta: Proposta;
}

export interface Plano {
  readonly exercicio: number;
  readonly orcamento: { readonly versao: { readonly id: string; readonly versao: number; readonly nome: string }; readonly cenario: { readonly id: string; readonly nome: string } | null; readonly frase: string } | null;
  readonly caixa: { readonly valor: number; readonly data: string; readonly saldoInformado: boolean; readonly naoEntrou: readonly string[]; readonly capacidade: number | null; readonly horizonte: { readonly de: string; readonly ate: string } } | null;
  readonly ranking: readonly LinhaDoPlano[];
  readonly recusadas: readonly Proposta[];
  readonly capacidadePorTipo: readonly { readonly tipo: string; readonly maquinas: number; readonly capacidadeHorasMes: number | null; readonly demandaHorasMes: number | null; readonly falta: number | null }[];
  readonly criteriosVigentes: number;
  readonly naoInclui: readonly string[];
  readonly frases: { readonly candidata: string };
}

export interface Opcoes {
  readonly maquinas: readonly { readonly id: string; readonly codigo: string; readonly descricao: string; readonly tipo: string | null }[];
  readonly tipos: readonly string[];
  readonly estudosConcluidos: readonly { readonly id: string; readonly numero: number; readonly equipamentoId: string; readonly codigo: string; readonly alternativa: string }[];
  readonly centros: readonly { readonly id: string; readonly codigo: string; readonly nome: string }[];
}

export interface BuscaDaFrota {
  readonly pagina?: number;
  readonly porPagina?: number;
  readonly ordem?: OrdemDaFrota;
  readonly busca?: string;
  readonly tipo?: string;
  readonly filtro?: FiltroDaFrota;
}

export interface NovaProposta {
  readonly tipo: TipoDeProposta;
  readonly equipamentoId: string | null;
  readonly tipoDeMaquina: string | null;
  readonly estudoId: string | null;
  readonly descricao: string;
  readonly investimento: number | null;
  readonly compromissoMensal: number | null;
  readonly mesesDeCompromisso: number | null;
  readonly dataPretendida: string;
  readonly risco: Risco;
  readonly justificativaDoRisco: string;
}

export interface RespostaDaPremissa {
  readonly estudoId: string;
  readonly numero: number;
  readonly codigo: string;
  readonly premissa: string;
  readonly unidade: string;
  readonly antes: number | null;
  readonly depois: number | null;
  readonly fonte: string | null;
  readonly alternativasCalculadasAntes: number;
  readonly alternativasCalculadas: number;
}

const id = (v: string) => encodeURIComponent(v);

function consulta(b: BuscaDaFrota): string {
  const p = new URLSearchParams();
  if (b.pagina) p.set('pagina', String(b.pagina));
  if (b.porPagina) p.set('porPagina', String(b.porPagina));
  if (b.ordem) p.set('ordem', b.ordem);
  if (b.busca) p.set('busca', b.busca);
  if (b.tipo) p.set('tipo', b.tipo);
  if (b.filtro) p.set('filtro', b.filtro);
  const s = p.toString();
  return s ? `?${s}` : '';
}

export const apiDaRenovacao = {
  frota: (b: BuscaDaFrota = {}) => pedir<Frota>(`/renovacao/frota${consulta(b)}`),
  historico: (equipamentoId: string) => pedir<HistoricoDaMaquina>(`/renovacao/maquinas/${id(equipamentoId)}/historico`),
  estudos: (situacao?: SituacaoDoEstudo) => pedir<{ estudos: readonly EstudoNaLista[] }>(`/renovacao/estudos${situacao ? `?situacao=${situacao}` : ''}`),
  estudo: (e: string) => pedir<Estudo>(`/renovacao/estudos/${id(e)}`),
  criarEstudo: (equipamentoId: string, horizonteMeses: number) =>
    comCorpo<{ id: string; numero: number; codigo: string; horizonteMeses: number; preenchidas: number; daOrigem: readonly string[]; avisos: readonly string[] }>('POST', '/renovacao/estudos', { equipamentoId, horizonteMeses }),
  ajustarEstudo: (e: string, horizonteMeses: number) =>
    pedir<{ id: string; numero: number; codigo: string; antes: number; depois: number }>(`/renovacao/estudos/${id(e)}`, { method: 'PUT', body: JSON.stringify({ horizonteMeses }) }),
  gravarPremissa: (e: string, codigo: string, valor: number | null, fonte: string | null) =>
    pedir<RespostaDaPremissa>(`/renovacao/estudos/${id(e)}/premissas`, {
      method: 'PUT',
      body: JSON.stringify(valor === null ? { codigo, valor: null } : { codigo, valor, fonte: (fonte ?? '').trim() }),
    }),
  puxarDoOrcamento: (e: string, cenarioId: string | null) =>
    comCorpo<{ id: string; numero: number; codigo: string; substituidas: number; preenchidas: number; avisos: readonly string[] }>('POST', `/renovacao/estudos/${id(e)}/orcamento`, { cenarioId }),
  concluir: (e: string, alternativa: Alternativa, conclusao: string) =>
    comCorpo<{ id: string; numero: number; codigo: string; alternativa: string; custoPorHora: number | null; total: number | null; candidata: boolean; concluidoPor: string | null }>('POST', `/renovacao/estudos/${id(e)}/conclusao`, { alternativa, conclusao: conclusao.trim() }),
  arquivar: (e: string, motivo: string) => comCorpo<{ id: string; numero: number; codigo: string; motivo: string }>('POST', `/renovacao/estudos/${id(e)}/arquivo`, { motivo: motivo.trim() }),
  criterios: () => pedir<{ criterios: readonly Criterio[]; temLicencaDeAprovar: boolean }>('/renovacao/criterios'),
  proporCriterio: (c: { nome: string; descricao: string; peso: number; desde: string; ate: string | null }) =>
    comCorpo<{ id: string; nome: string; peso: number; desde: string; ate: string | null }>('POST', '/renovacao/criterios', { ...c, nome: c.nome.trim(), descricao: c.descricao.trim() }),
  aprovarCriterio: (c: string, nota: string) => comCorpo<{ id: string; nome: string; peso: number; aprovadoPor: string | null; vigentes: number }>('POST', `/renovacao/criterios/${id(c)}/aprovacao`, { nota: nota.trim() }),
  retirarCriterio: (c: string, motivo: string) => comCorpo<{ id: string; nome: string; motivo: string }>('POST', `/renovacao/criterios/${id(c)}/retirada`, { motivo: motivo.trim() }),
  propostas: () => pedir<Propostas>('/renovacao/propostas'),
  propor: (p: NovaProposta) =>
    comCorpo<{ id: string; numero: number; tipo: string; maquina: string | null; tipoDeMaquina: string | null; investimento: number | null; compromissoMensal: number | null; dataPretendida: string }>('POST', '/renovacao/propostas', {
      ...p,
      descricao: p.descricao.trim(),
      justificativaDoRisco: p.justificativaDoRisco.trim(),
    }),
  darNota: (p: string, criterioId: string, nota: number, justificativa: string) =>
    comCorpo<{ proposta: number; criterio: string; nota: number; antes: number | null; pontuacao: number | null }>('POST', `/renovacao/propostas/${id(p)}/notas`, { criterioId, nota, justificativa: justificativa.trim() }),
  decidir: (p: string, decisao: Exclude<SituacaoDaProposta, 'proposta'>, nota: string) =>
    comCorpo<{ id: string; numero: number; descricao: string; antes: SituacaoDaProposta; depois: SituacaoDaProposta; investimento: number | null; decididaPor: string | null }>('POST', `/renovacao/propostas/${id(p)}/decisao`, { decisao, nota: nota.trim() }),
  levarAoOrcamento: (p: string, centroCustoId: string, abrirRevisao: boolean) =>
    comCorpo<{ linhaId: string; proposta: number; exercicio: number; versao: number; revisaoAberta: number | null; centro: string | null; competencia: string; valor: number | null }>('POST', `/renovacao/propostas/${id(p)}/orcamento`, { centroCustoId, abrirRevisao }),
  plano: () => pedir<Plano>('/renovacao/plano'),
  opcoes: () => pedir<Opcoes>('/renovacao/opcoes'),
};
